import { HttpInterceptorFn, HttpContextToken, HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, throwError, switchMap, BehaviorSubject, Subject } from 'rxjs';
import { UserService } from '../services/user-service/user-service';
import { AuthService } from '@/app/features/pre-login/services/login-service/auth-service';
import { SnackbarService } from '../services/snackbar-service/snackbar-service';
import { Router } from '@angular/router';
import { LoaderService } from '../services/loader-service/loader-service';
import { REFRESH_REQ } from './http-context.token';

const authError$ = new BehaviorSubject<boolean>(false);

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);
  const snackbarService = inject(SnackbarService);
  const loaderService = inject(LoaderService);

  const wait = (): Observable<boolean> => {
    const subject = new Subject<boolean>();
    const subscription = authError$.subscribe((error: boolean) => {
      if (!error) {
        subject.next(true);
        subscription.unsubscribe();
      }
    });
    return subject.asObservable();
  };

  return next(req).pipe(
    catchError((e) => {
      const isLoginRequest = req.url.includes('/auth/login');

      if (req.context.get(REFRESH_REQ)) {
        return throwError(() => e);
      }

      if (e.status === 401 && !isLoginRequest) {
        const isScopeTokenRoute =
          req.url.includes('verify-mfa') ||
          req.url.includes('setup-mfa') ||
          req.url.includes('resend-mfa');

        if (isScopeTokenRoute) {
          // Let the component handle expired token, don't refresh or logout
          return throwError(() => e);
        }

        if (!authError$.getValue()) {
          authError$.next(true);
          return authService.refreshAccessToken().pipe(
            switchMap((res: any) => {
              userService.setAccessToken(res?.data?.accessToken);
              authError$.next(false);
              const newRequest = req.clone({
                setHeaders: {
                  Authorization: 'Bearer ' + userService.getAccessToken(),
                },
              });
              return next(newRequest);
            }),
            catchError((error: any) => {
              loaderService.show('Logging out...');
              userService.clearUser();
              authError$.next(false);
              router.navigate(['/auth/login']);
              setTimeout(() => loaderService.hide(), 2000);
              return throwError(() => error);
            })
          );
        } else {
          return wait().pipe(
            switchMap(() => {
              const newRequest = req.clone({
                setHeaders: {
                  Authorization: 'Bearer ' + userService.getAccessToken(),
                },
              });
              return next(newRequest);
            })
          );
        }
      } else if (e.status === 503) {
        // router.navigate(['/service-unavailable']);
        return throwError(() => e);
      } else {
        return throwError(() => e);
      }
    })
  );
};
